import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { RefreshCw, ChevronRight, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { Btn, Badge } from "./common";

/**
 * Painel de log de webhooks reutilizavel.
 * Usado dentro das paginas dedicadas (CogniEtica, Privaxia) e da pagina dinamica (DynamicConnectorPage).
 * Filtra pelo `origem` (slug) e mostra Data | Origem -> Destino | Evento | Payload expandivel.
 */
export function WebhookLogPanel({ origem, title = "Comunicacoes recebidas", pageSize = 20 }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState("");
  const [dataIni, setDataIni] = useState("");
  const [dataFim, setDataFim] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { origem, limit: pageSize };
      if (event) params.event = event;
      if (dataIni) params.data_ini = dataIni;
      if (dataFim) params.data_fim = dataFim;
      const { data } = await api.get("/webhook-events", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [origem]);

  return (
    <div data-testid={`whlog-${origem}`}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-sm text-muted2">{title} - <b className="text-navy">{total}</b> registados</div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="block text-[10px] font-semibold text-muted2 uppercase tracking-wide mb-0.5">Evento</label>
            <input value={event} onChange={(e) => setEvent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="ex: tenant.created" className="px-2 py-1 border border-tint rounded text-xs w-40" data-testid={`whlog-${origem}-event`} />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-muted2 uppercase tracking-wide mb-0.5">De</label>
            <input type="date" value={dataIni} onChange={(e) => setDataIni(e.target.value)}
              className="px-2 py-1 border border-tint rounded text-xs" data-testid={`whlog-${origem}-dataini`} />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-muted2 uppercase tracking-wide mb-0.5">Ate</label>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)}
              className="px-2 py-1 border border-tint rounded text-xs" data-testid={`whlog-${origem}-datafim`} />
          </div>
          <Btn variant="ghost" onClick={load} testid={`whlog-${origem}-refresh`}>
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Atualizar
          </Btn>
        </div>
      </div>

      <div className="bg-white border border-tint rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-tint">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2 w-40">Quando</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2">Origem &rarr; Destino</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2 w-64">Evento</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase font-semibold text-muted2 w-56">Estado HTTP</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="text-center text-muted2 py-8" data-testid={`whlog-${origem}-empty`}>Sem eventos.</td></tr>
            )}
            {rows.map((r) => {
              const open = expanded === r.id;
              return (
                <>
                  <tr key={r.id} onClick={() => setExpanded(open ? null : r.id)}
                    className="border-b border-tint hover:bg-slate-50/50 cursor-pointer" data-testid={`whlog-row-${r.id}`}>
                    <td className="px-3 py-1.5 text-xs text-muted2 whitespace-nowrap">
                      {r.em ? new Date(r.em).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "medium" }) : "-"}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-mono uppercase">{r.origem_slug}</span>
                        <ArrowRight size={11} className="text-muted2" />
                        <code className="text-[10px] bg-tint px-1 py-0.5 rounded">{r.destino}</code>
                      </div>
                    </td>
                    <td className="px-3 py-1.5"><code className="text-[11px] bg-slate-100 text-navy px-1.5 py-0.5 rounded">{r.event}</code></td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-col">
                        <span className={`text-[10px] font-mono font-bold ${r.http_status >= 400 ? "text-red-600" : "text-emerald-600"}`}>{r.http_status}</span>
                        <span className="text-[9px] text-muted2 leading-tight">{r.http_status_label}</span>
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-muted2">
                      <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} />
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50/70 border-b border-tint">
                      <td colSpan={5} className="px-3 py-2">
                        {r.identifier && <div className="text-xs mb-1"><span className="text-muted2">Identificador:</span> <code>{r.identifier}</code></div>}
                        {r.status && <div className="text-xs mb-1"><span className="text-muted2">Status:</span> <Badge value={r.status} /></div>}
                        <pre className="text-[10px] bg-navy text-white/90 p-2 rounded overflow-x-auto max-h-56">{JSON.stringify(r.payload, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
